/**
 * Layout genérico dos hubs de cadastro (Cadastros e Categorias): duas colunas —
 * nav interna à esquerda (lista dos tipos) + conteúdo à direita (`<Outlet/>` = a
 * ResourceListPage do tipo selecionado). Padrão da referência preto/branco do
 * mock, com a identidade "folha" da TransGrãos aplicada de forma sutil.
 *
 * O topbar (título/eyebrow) é preenchido pela própria ResourceListPage via
 * PageHeader → aqui não repetimos cabeçalho.
 */
import Box from '@mui/material/Box'
import { Navigate, NavLink, Outlet } from 'react-router-dom'
import type { LucideIcon } from 'lucide-react'
import { useAuth } from '@/core/auth/useAuth'
import { useCan } from '@/core/auth/useCan'
import { homePathFor } from '@/core/router/paths'
import { LEAF_RADIUS_SM, EASE_ORGANIC, layout } from '../../styles/theme'

export interface HubNavItem {
  label: string
  /** Rota absoluta do tipo, ex.: '/logistica/cadastros/customers'. */
  to: string
  icon: LucideIcon
  /** Key exigida para a entrada aparecer. Sem ela, a entrada é pública ao hub.
   *
   *  Existe porque estes hubs tinham lista PRÓPRIA, sem checagem nenhuma: a
   *  entrada "Usuários" aparecia para qualquer pessoa que alcançasse Cadastros,
   *  mesmo com "Ver usuários" desmarcado. Era o defeito relatado. */
  permission?: string
}

/** Entrada do hub como a área a declara: a chave do rótulo no lugar do texto. */
export type HubItemDef = Omit<HubNavItem, 'label'> & { labelKey: string }

/** O índice do hub abre a primeira aba que a pessoa alcança. Era fixo na primeira
 *  aba da lista, e quem não tinha a key dela caía no portão e saía do hub. Sem
 *  nenhuma aba ao alcance, vale a home. */
export function HubIndexRedirect({ itens }: { itens: Array<Pick<HubNavItem, 'to' | 'permission'>> }) {
  const can = useCan()
  const { user } = useAuth()
  const primeira = itens.find((item) => !item.permission || can(item.permission))
  return <Navigate to={primeira ? primeira.to : homePathFor(user)} replace />
}

const navItemSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  px: 1.25,
  py: 0.75,
  minHeight: 38,
  flex: { xs: '0 0 auto', md: '0 0 auto' },
  borderRadius: LEAF_RADIUS_SM,
  color: 'text.secondary',
  textDecoration: 'none',
  fontSize: '0.875rem',
  fontWeight: 500,
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
  position: 'relative',
  transition: `background-color 150ms ${EASE_ORGANIC}, color 150ms ${EASE_ORGANIC}`,
  '& svg': { flexShrink: 0, color: 'inherit' },
  '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
  // Ativo discreto (estilo referência): fundo suave + texto em verde + barra-guia fina.
  '&.active': {
    bgcolor: 'action.selected',
    color: 'primary.main',
    fontWeight: 600,
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 8,
      bottom: 8,
      width: 2,
      borderRadius: 2,
      backgroundColor: 'primary.main',
    },
  },
} as const

export function CadastroHubLayout({ eyebrow, items }: { eyebrow: string; items: HubNavItem[] }) {
  const can = useCan()
  // Mesmo filtro da barra lateral (shared/layout/SidebarContent.tsx). O hub não
  // pode ser uma segunda lista de entradas com critério próprio.
  const visiveis = items.filter((item) => !item.permission || can(item.permission))
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '200px minmax(0, 1fr)' },
        gap: { xs: 2, md: 3 },
        alignItems: 'start',
      }}
    >
      <Box
        component="nav"
        aria-label={eyebrow}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'row', md: 'column' },
          gap: 0.25,
          overflowX: { xs: 'auto', md: 'visible' },
          pb: { xs: 0.5, md: 0 },
          // Trilho fica visível ao rolar a tabela (sticky abaixo do topbar).
          position: { md: 'sticky' },
          top: { md: `calc(${layout.topbarHeight}px + 16px)` },
        }}
      >
        {visiveis.map((item) => {
          const Icon = item.icon
          return (
            <Box key={item.to} component={NavLink} to={item.to} end sx={navItemSx}>
              <Icon size={16} strokeWidth={2} />
              <Box component="span">{item.label}</Box>
            </Box>
          )
        })}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Outlet />
      </Box>
    </Box>
  )
}
